import { useRef, useState } from 'react'
import {
    Button,
    Card,
    CardContent,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@duck/ui'
import { apis, useTar, TranslationKey } from '@/modules'
import { UserService } from '@duck/api-definitions'

// Icon mapping for different sources
const sourceIconMap: Record<string, React.ReactNode> = {
    [UserService.HeardOfUsSource.Instagram]: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
        </svg>
    ),
    [UserService.HeardOfUsSource.Facebook]: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
    ),
    [UserService.HeardOfUsSource.Linkedin]: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
    ),
    [UserService.HeardOfUsSource.Reddit]: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 3.243.913.759 0 2.401-.071 3.243-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.808.73-1.124 0-2.261-.197-2.808-.73a.326.326 0 0 0-.232-.095z" />
        </svg>
    ),
    [UserService.HeardOfUsSource.X]: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
    ),
    [UserService.HeardOfUsSource.ChatGpt]: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M11.586 2.836l-9.024 3.852c-.377.161-.596.558-.562.971.034.413.318.766.705.905l7.338 2.778v8.658c0 .414.448.793.955.676.507-.117.885-.569.885-1.057V11.15c0-.11-.015-.216-.044-.32l-1.507-5.062L12.4 5.71c.377-.16.596-.557.563-.97-.034-.413-.318-.766-.705-.905z" />
        </svg>
    ),
    [UserService.HeardOfUsSource.Gemini]: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2L2 7l10 5 10-5-10-5z" />
            <path d="M2 17l10 5 10-5" />
            <path d="M2 12l10 5 10-5" />
        </svg>
    ),
    [UserService.HeardOfUsSource.Claude]: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
        </svg>
    ),
    [UserService.HeardOfUsSource.Google]: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="currentColor" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="currentColor" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="currentColor" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="currentColor" />
        </svg>
    ),
    [UserService.HeardOfUsSource.Bing]: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M5.71 3l12.58 10-.19.22c-.19.22-.41.38-.67.52l-.16.09-3.52 1.97c-.11.06-.24.06-.35 0l-3.52-1.97c-.36-.2-.66-.52-.86-.91l-.01-.03L3.76 9.14l-.02-.01c-.06-.12-.06-.25 0-.37l.01-.01L5.71 3z" />
        </svg>
    ),
    [UserService.HeardOfUsSource.Yandex]: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h2.08c.1 1.05.82 1.87 2 2v-4H9.59V8.91h4.56c.68 0 1.14.47 1.41.81v3.9c1.12-.03 1.94-.88 2-2h2.02c-.09 1.96-1.57 3.05-3.17 3.28z" />
        </svg>
    ),
    [UserService.HeardOfUsSource.Email]: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
        </svg>
    ),
    [UserService.HeardOfUsSource.WordOfMouth]: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" />
        </svg>
    ),
}

interface AllFormData {
    email?: string
    firstName?: string
    lastName?: string
    hearAbout?: string
}

export const WaitingList = () => {
    const { t } = useTar()
    const [isEmailSubmitted, setIsEmailSubmitted] = useState(false)
    const [isMoreInfoSubmitted, setIsMoreInfoSubmitted] = useState(false)
    const [allFormData, setAllFormData] = useState<AllFormData | null>(null)
    const [hearAboutValue, setHearAboutValue] = useState<UserService.HeardOfUsSource | undefined>(undefined)
    const waitingListIdRef = useRef<string | null>(null)

    const handleEmail = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        const formData = new FormData(e.currentTarget)
        const email = formData.get('email') as string
        setAllFormData({ email })
        setIsEmailSubmitted(true)
        const { data } = await apis.userService.waitingListPost({ email })
        waitingListIdRef.current = data.waitingListId
    }

    const HandleMoreInfo = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        const formData = new FormData(e.currentTarget)
        const firstName = formData.get('firstName') as string
        const lastName = formData.get('lastName') as string
        const heardOfUsOther = formData.get('heardOfUsOther') as string
        if (allFormData?.email && waitingListIdRef.current) {
            await apis.userService.waitingListPost({
                email: allFormData.email,
                firstName,
                lastName,
                heardOfUs: hearAboutValue,
                heardOfUsOther: heardOfUsOther || undefined,
                waitingListId: waitingListIdRef.current
            })
        }
        setIsMoreInfoSubmitted(true)
    }

    return (
        <div className="flex flex-col items-center justify-center space-y-4">
            {!isEmailSubmitted && (
                <>
                    <div className="space-y-8">
                        <div className="text-center">
                            <h2 className="text-3xl font-bold text-foreground mb-4">
                                {t('common.joinWaitingList')}
                            </h2>
                            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                                {t('common.joinWaitingListDescription')}
                            </p>
                        </div>
                    </div>
                    <form onSubmit={handleEmail} className="space-y-2 w-full max-w-sm">
                        <input
                            type="email"
                            name="email"
                            autoComplete="email"
                            required
                            placeholder={t('common.email')}
                            className={`w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground
                        placeholder-muted focus:outline-none focus:ring-2 focus:ring-ring 
                        focus:border-accent transition-colors`}
                        />
                        <Button
                            type="submit"
                            variant="default"
                            className="w-full"
                        >
                            {t('common.subscribe')}
                        </Button>
                    </form>
                </>
            )}

            {isEmailSubmitted && !isMoreInfoSubmitted && (
                <div className="space-y-8 max-w-sm">
                    <Card className="max-w-md mx-auto bg-success/10 border border-success/20">
                        <CardContent className="pt-6">
                            <p className="text-center text-success">
                                {t('common.joinedWaitingListSuccessfully')}
                            </p>
                        </CardContent>
                    </Card>

                    <form onSubmit={HandleMoreInfo} className="mt-4 space-y-2 w-full max-w-md mx-auto flex flex-col items-center justify-center">
                        <p className="font-medium text-center">
                            {t('common.helpUsImprove')}
                        </p>
                        <Select value={hearAboutValue} onValueChange={(value) => setHearAboutValue(value as UserService.HeardOfUsSource)} required>
                            <SelectTrigger className="w-full">
                                <SelectValue placeholder={t('common.whereDidYouHearAboutUs')} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={UserService.HeardOfUsSource.Linkedin}>
                                    <div className="flex items-center gap-2">
                                        {sourceIconMap[UserService.HeardOfUsSource.Linkedin]}
                                        <span>{t('common.linkedin')}</span>
                                    </div>
                                </SelectItem>
                                <SelectItem value={UserService.HeardOfUsSource.Reddit}>
                                    <div className="flex items-center gap-2">
                                        {sourceIconMap[UserService.HeardOfUsSource.Reddit]}
                                        <span>{t('common.reddit' as TranslationKey)}</span>
                                    </div>
                                </SelectItem>
                                <SelectItem value={UserService.HeardOfUsSource.X}>
                                    <div className="flex items-center gap-2">
                                        {sourceIconMap[UserService.HeardOfUsSource.X]}
                                        <span>Twitter (currently X)</span>
                                    </div>
                                </SelectItem>
                                <SelectItem value={UserService.HeardOfUsSource.Google}>
                                    <div className="flex items-center gap-2">
                                        {sourceIconMap[UserService.HeardOfUsSource.Google]}
                                        <span>{t('common.google')}</span>
                                    </div>
                                </SelectItem>
                                <SelectItem value={UserService.HeardOfUsSource.WordOfMouth}>
                                    <div className="flex items-center gap-2">
                                        {sourceIconMap[UserService.HeardOfUsSource.WordOfMouth]}
                                        <span>{t('common.wordOfMouth')}</span>
                                    </div>
                                </SelectItem>
                                <SelectItem value={UserService.HeardOfUsSource.Other}>
                                    <div className="flex items-center gap-2">
                                        <span>{t('common.other')}</span>
                                    </div>
                                </SelectItem>
                            </SelectContent>
                        </Select>
                        {hearAboutValue === UserService.HeardOfUsSource.Other && (
                            <input
                                type="text"
                                name="heardOfUsOther"
                                placeholder={t('common.pleaseSpecify')}
                                className={`w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground
                                    placeholder-muted focus:outline-none focus:ring-2 focus:ring-ring 
                                    focus:border-accent transition-colors`}
                            />
                        )}
                        <input
                            type="text"
                            name="firstName"
                            autoComplete="given-name"
                            placeholder={t('common.firstName')}
                            className={`w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground
                                placeholder-muted focus:outline-none focus:ring-2 focus:ring-ring 
                                focus:border-accent transition-colors`}
                        />
                        <input
                            type="text"
                            name="lastName"
                            autoComplete="family-name"
                            placeholder={t('common.lastName')}
                            className={`w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground
                                placeholder-muted focus:outline-none focus:ring-2 focus:ring-ring 
                                focus:border-accent transition-colors`}
                        />
                        <Button
                            type="submit"
                            className="w-full text-foreground"
                        >
                            {t('common.submit')}
                        </Button>
                    </form>
                </div>
            )}

            {isMoreInfoSubmitted && (
                <div className="text-center max-w-md mx-auto">
                    <h2 className="text-3xl font-bold text-foreground mb-4">
                        {t('common.thankYou')}
                    </h2>
                    <p className="text-lg text-muted-foreground">
                        {t('common.appreciateInterest')}
                    </p>
                </div>
            )}
        </div >
    )
}